import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import type { MessageTemplate, ParameterSet, Dataset } from '@k-ucap/shared';
import { useState } from 'react';

interface GeneratorAssetSelectorProps {
  templates: MessageTemplate[];
  parameterSets: ParameterSet[];
  datasets: Dataset[];
  selectedTemplate: MessageTemplate | null;
  selectedParameterSet: ParameterSet | null;
  selectedDataset: Dataset | null;
  onSelectTemplate: (template: MessageTemplate | null) => void;
  onSelectParameterSet: (parameterSet: ParameterSet | null) => void;
  onSelectDataset: (dataset: Dataset | null) => void;
  onSaveAsTemplate: (name: string, description: string) => Promise<MessageTemplate | null>;
  disabled?: boolean;
}

export const GeneratorAssetSelector = ({
  templates,
  parameterSets,
  datasets,
  selectedTemplate,
  selectedParameterSet,
  selectedDataset,
  onSelectTemplate,
  onSelectParameterSet,
  onSelectDataset,
  onSaveAsTemplate,
  disabled = false,
}: GeneratorAssetSelectorProps) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleTemplateChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const template = templates.find((t) => t.id === id) ?? null;
    onSelectTemplate(template);
  };

  const handleParameterSetChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const parameterSet = parameterSets.find((p) => p.id === id) ?? null;
    onSelectParameterSet(parameterSet);
  };

  const handleDatasetChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const dataset = datasets.find((d) => d.id === id) ?? null;
    onSelectDataset(dataset);
  };

  const handleSave = async () => {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      await onSaveAsTemplate(templateName.trim(), templateDescription.trim());
      setSaveDialogOpen(false);
      setTemplateName('');
      setTemplateDescription('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        시험 자산 (Template / Parameter Set / Dataset)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 220 }} size="small" disabled={disabled}>
          <InputLabel id="template-select-label">Message Template</InputLabel>
          <Select
            labelId="template-select-label"
            value={selectedTemplate?.id ?? ''}
            label="Message Template"
            onChange={handleTemplateChange}
          >
            <MenuItem value="">
              <em>선택 안 함</em>
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name} <Chip size="small" label={`v${template.version}`} sx={{ ml: 1 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }} size="small" disabled={disabled}>
          <InputLabel id="paramset-select-label">Parameter Set</InputLabel>
          <Select
            labelId="paramset-select-label"
            value={selectedParameterSet?.id ?? ''}
            label="Parameter Set"
            onChange={handleParameterSetChange}
          >
            <MenuItem value="">
              <em>선택 안 함</em>
            </MenuItem>
            {parameterSets.map((ps) => (
              <MenuItem key={ps.id} value={ps.id}>
                {ps.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }} size="small" disabled={disabled}>
          <InputLabel id="dataset-select-label">Dataset</InputLabel>
          <Select
            labelId="dataset-select-label"
            value={selectedDataset?.id ?? ''}
            label="Dataset"
            onChange={handleDatasetChange}
          >
            <MenuItem value="">
              <em>선택 안 함</em>
            </MenuItem>
            {datasets.map((dataset) => (
              <MenuItem key={dataset.id} value={dataset.id}>
                {dataset.name} <Chip size="small" label={`${dataset.rowCount} rows`} sx={{ ml: 1 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveIcon />}
          onClick={() => setSaveDialogOpen(true)}
          disabled={disabled}
        >
          Save as Template
        </Button>
      </Box>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save as Message Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!templateName.trim() || saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
